import _ from 'lodash';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { AIDisclosureResultState } from '../../types';
import { AIDisclosureParagraphDefaultStepType, type AIDisclosureParagraphEditType, AIDisclosureParagraphType, AIDisclosureStep } from '../../enums';
import { AIDisclosureResultModel } from '../../models/disclosure-result-model';
import {
    GetTechMeansDTO,
    PutDisclosureSettingDTO,
    GetDisclosureTitleDTO,
} from '@/domains/disclosure/repository/disclosure-repository';
import { AIDisclosureModel } from '@/domains/disclosure/models/disclosure-model';
import { StreamStatus } from '@/domains/disclosure/enums';
import {
    type ApiGetDisclosureContentRequest, type ApiGetDisclosureContentResponse,
    GetDisclosureContent,
} from '@/infrastructure/api-functions/core-drafting-api/get-disclosure-content-api';
import { I18nService } from '@telecom/shared/services/i18n';
import { Toast } from 'patsnap-biz';
import { AIDisclosureResultService } from '../disclosure-result-service';
import { UpdateTaskContentApi } from '@/infrastructure/api-functions/core-drafting-api/update-task-content-api';
import type { PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import { RegenerateContentApi } from '@/infrastructure/api-functions/core-drafting-api/regenerate-content-api';
import { ContentHideApi } from '@/infrastructure/api-functions/core-drafting-api/content-hide-api';
import { ReferenceAnchorIndexGenerator } from '@telecom/shared/services/reference-anchor/reference-anchor';

@injectable()
export class AIDisclosureResultServiceImpl implements AIDisclosureResultService {
    private _stream: PsHttpSSE<ApiGetDisclosureContentResponse> | null = null;
    private _onStatesChangeEmitter = new Emitter<AIDisclosureResultState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    // eslint-disable-next-line max-params
    constructor(
        @inject(AIDisclosureModel)
        private _disclosureModel: AIDisclosureModel,
        @inject(AIDisclosureResultModel)
        private _model: AIDisclosureResultModel,
        @inject(GetTechMeansDTO)
        private _getTechMeansDTO: GetTechMeansDTO,
        @inject(GetDisclosureContent)
        private _getDisclosureContent: GetDisclosureContent,
        @inject(PutDisclosureSettingDTO)
        private _putDisclosureSettingDTO: PutDisclosureSettingDTO,
        @inject(UpdateTaskContentApi)
        private _updateTaskContentApi: UpdateTaskContentApi,
        @inject(GetDisclosureTitleDTO)
        private _getDisclosureTitleDTO: GetDisclosureTitleDTO,
        @inject(RegenerateContentApi)
        private _regenerateContentApi: RegenerateContentApi,
        @inject(ContentHideApi)
        private _contentHideApi: ContentHideApi,
        @inject(I18nService)
        private _i18n: I18nService,
        @inject(ReferenceAnchorIndexGenerator)
        private _referenceAnchorIndexGenerator: ReferenceAnchorIndexGenerator,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<AIDisclosureResultState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public initModel(modelData: Partial<AIDisclosureResultState>) {
        this._setModelData(modelData);
    }

    public updateTitle(newTitle: string) {
        const { taskId } = this._getTaskInfo();
        const params = {
            taskId: taskId,
            contentType: AIDisclosureParagraphDefaultStepType.TITLE,
            content: newTitle,
        };

        this._setModelData({
            title: newTitle,
        });

        this._updateTaskContentApi.updateTaskContent(params);
    }

    public async updateSetting(settings: AIDisclosureResultState['settings'], init = false) {
        const { taskId } = this._getTaskInfo();
        init && await this._putDisclosureSettingDTO.fetch({ ...settings, taskId, currentStep: AIDisclosureStep.DISCLOSURE_RESULT });
        this._setModelData({
            settings,
        });
    }

    public toggleParagraphVisibleStatus(type: AIDisclosureParagraphType) {
        const { hidden } = this.getModelData();
        const { taskId } = this._getTaskInfo();
        const index = hidden.indexOf(type);
        if (index !== -1) {
            hidden.splice(index, 1);
        } else {
            hidden.push(type);
        }
        this._setModelData({ hidden });
        const params = {
            task_id: taskId,
            content_type: type,
            hidden: !!_.find(hidden, item => item === type),
        };
        this._contentHideApi.contentHideApi(params);
    }

    private _getTaskInfo() {
        const { currentStep, taskId, stepDetail, input} = this._disclosureModel.modelData;
        const model = this.getModelData();
        return { taskId, currentStep, stepDetail, input, ...model };
    }

    private async _getDisclosureTitle() {
        const { taskId, currentStep } = this._getTaskInfo();
        const disclosureTitle = await this._getDisclosureTitleDTO.fetch({ taskId, currentStep });

        this._setModelData({
            title: disclosureTitle,
        });
    }

    public updateDisclosureResult(paragraphType: AIDisclosureParagraphType, content: string, isAddition: boolean = false) {
        const { disclosureResult } = this.getModelData();
        const { taskId } = this._getTaskInfo();
        const index = disclosureResult.findIndex(item => item.paragraphType === paragraphType);
        if (index !== -1) {
            if (isAddition) {
                disclosureResult[index].content += content;
            } else {
                disclosureResult[index].content = content;
            }

            this._setModelData({ disclosureResult });

            const params = {
                taskId: taskId,
                contentType: paragraphType,
                content: disclosureResult[index].content,
            };

            this._updateTaskContentApi.updateTaskContent(params);
        }
    }

    public stopGenerate() {
        this._stream?.close();
    }

    public async generateContent(params: {
        contentType: AIDisclosureParagraphEditType,
        text?: string
    }, callback: (status: StreamStatus, content?: string) => void) {
        const { contentType, text } = params;
        const { taskId, currentStep } = this._getTaskInfo();

        this._stream = this._getDisclosureContent.getDisclosureContent({
            task_id: taskId,
            current_step: currentStep,
            content_type: contentType,
            ...(text ? { text } : {}),
        })
            .onMessage(data => {
                const { status, content } = data.data ?? {};
                if (status === StreamStatus.FINISH) {
                    callback(status, content);
                    this._stream?.close();
                } else {
                    if (content.length) {
                        callback(status, content);
                    }
                }
            })
            .onError((err) => {
                // eslint-disable-next-line
                callback('ERROR');
                Toast({
                    message: this._i18n.t(`commonStatusCode.${err?.error_code || 400}`),
                    type: 'error',
                    duration: 5000,
                });
            });

        this._stream && this._stream.start();
    }

    public async regenerateContent() {
        const { taskId } = this._getTaskInfo();
        await this._regenerateContentApi.regenerateContent({task_id: taskId});
    }

    public async generateResult(init = false) {
        this._referenceAnchorIndexGenerator.reset();
        const { taskId, currentStep, stepDetail, title, settings, input } = this._getTaskInfo();

        this._setModelData({ disclosureResult: [] });

        if (!settings.techMeans) {
            const settings = await this._getTechMeansDTO.fetch({ taskId, currentStep, input });
            this._setModelData({
                settings,
            });
        }

        if (!title) {
            await this._getDisclosureTitle();
        }

        let steps = _.chain(stepDetail[AIDisclosureStep.DISCLOSURE_RESULT])
            .filter(step => _.includes(Object.values(AIDisclosureParagraphType), step.content_type))
            .map(step => step.content_type)
            .value();

        steps = (_.isEmpty(steps) || init) ? Object.values(AIDisclosureParagraphType) : steps;

        for (const paragraphType of steps) {
            await this._fetchStreamData({
                task_id: taskId,
                current_step: currentStep,
                content_type: paragraphType,
            });

            this._updateDisclosureResult({ paragraphType, isGenerated: true });
        }
    }

    public status = StreamStatus.PROGRESS_WITHOUT_DATA;

    private _updateDisclosureResult = ({ content = '', paragraphType, isGenerated = false }: { content?: string, paragraphType: AIDisclosureParagraphType, isGenerated?: boolean }) => {
        const { disclosureResult } = this.getModelData();

        const idx = disclosureResult.findIndex(item => item.paragraphType === paragraphType);

        if (isGenerated && disclosureResult[idx]) {
            // 控制下一段内容的title显示
            disclosureResult[idx].isGenerated = true;
        }

        if (content.length && !isGenerated) {
            if (idx !== -1) {
                disclosureResult[idx].content = content;
            } else {
                disclosureResult.push({
                    paragraphType: paragraphType,
                    isGenerated: false,
                    content: content,
                });
            }
        }

        this._setModelData({ disclosureResult });
    };

    private async _fetchStreamData(params: ApiGetDisclosureContentRequest<AIDisclosureParagraphType>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this._stream = this._getDisclosureContent.getDisclosureContent(params)
                .onMessage(data => {
                    const { content } = data.data ?? {};
                    content && this._updateDisclosureResult({ content, paragraphType: params.content_type });
                })
                .onError((err: any) => {
                    reject(err);
                })
                .onFinish(() => {
                    resolve();
                });

            this._stream && this._stream.start();
        });
    }
}
